import { OutputFilterHealthStatus, OutputsRequestParams } from '../../../api/nm-types'
import { FilterBy, FilterTemplate } from '../../common/Filters/FilterView/FilterTemplate'
import { ApplianceType, ExperimentalFeatures, OutputAdminStatus } from 'common/api/v1/types'
import { distinct } from 'common/utils'
import type { Api } from '../../../store'

export enum ListOutputFilterKey {
  appliance = 'appliance',
  name = 'name',
  healthStatus = 'health status',
  adminStatus = 'admin status',
  input = 'input',
  applianceRegion = 'appliance region',
  group = 'group',
  tag = 'tag',
}

// Constructs a list-Output-filter populated with possible values/suggestions
export function makeListOutputFilter({
  api,
  expFeatures,
}: {
  api: typeof Api
  expFeatures?: ExperimentalFeatures
}): FilterTemplate[] {
  const requestPaging = { pageNumber: '0', rowsPerPage: '20' }
  const supportsStatusFilter = expFeatures && expFeatures.ExtHealthAlarms
  const filterTemplates: FilterTemplate[] = [
    ...(supportsStatusFilter
      ? [
          {
            key: ListOutputFilterKey.healthStatus,
            operator: 'is',
            possibleValuesSync: (searchText) => {
              return [
                { value: OutputFilterHealthStatus.healthy, displayName: 'healthy' },
                { value: OutputFilterHealthStatus.unhealthy, displayName: 'unhealthy' },
              ].filter((v) => !searchText || v.displayName.includes(searchText))
            },
          } as FilterTemplate,
        ]
      : []),
    {
      key: ListOutputFilterKey.adminStatus,
      operator: 'is',
      possibleValuesSync: (searchText) => {
        return [
          { value: OutputAdminStatus.on.toString(), displayName: 'enabled' },
          { value: OutputAdminStatus.off.toString(), displayName: 'disabled' },
        ].filter((v) => !searchText || v.displayName.includes(searchText))
      },
    },
    { key: ListOutputFilterKey.name, operator: 'matches' },
    {
      key: ListOutputFilterKey.input,
      operator: 'is',
      possibleValues: async (searchText) => {
        const result = await api.inputApi.getBareInputs({
          ...requestPaging,
          filter: searchText,
        })
        return result.items.map((a) => ({ value: a.name, displayName: a.name }))
      },
    },
    {
      key: ListOutputFilterKey.applianceRegion,
      operator: 'isOneOf',
      possibleValues: async (searchText) => {
        const result = await api.regionApi.getRegions({
          ...requestPaging,
          filter: searchText,
        })
        return result.items.map((a) => ({ value: a.name, displayName: a.name }))
      },
    },
    {
      key: ListOutputFilterKey.appliance,
      operator: 'isOneOf',
      possibleValues: async (searchText) => {
        const result = await api.appliancesApi.getBareAppliances({
          ...requestPaging,
          filter: searchText,
        })
        return result.items
          .filter((a) => a.type !== ApplianceType.thumb)
          .map((a) => ({ value: a.name, displayName: a.name }))
      },
    },
    {
      key: ListOutputFilterKey.group,
      operator: 'is',
      possibleValues: async (searchText) => {
        const result = await api.groupsApi.getPureGroups({
          ...requestPaging,
          filter: searchText,
        })
        return result.items.map((a) => ({ value: a.name, displayName: a.name }))
      },
    },
    {
      key: ListOutputFilterKey.tag,
      operator: 'isOneOf',
      possibleValues: async (searchText) => {
        const { items: tags } = await api.tagApi.listTags({ filter: { searchName: searchText } })
        // Super user receives tags from all groups - no need to display duplicates
        const uniqueTags = distinct(tags, (a, b) => a.name === b.name)
        return uniqueTags.map(({ name }) => ({ value: name, displayName: name }))
      },
    },
  ]
  return filterTemplates.sort((a, b) => a.key.localeCompare(b.key))
}

export function mapOutputFilterToUrlParam(filter: FilterBy): Partial<OutputsRequestParams> {
  if ('value' in filter) {
    switch (filter.key) {
      case ListOutputFilterKey.name:
        return { filter: filter.value }
      case ListOutputFilterKey.healthStatus:
        return { health: filter.value }
      case ListOutputFilterKey.group:
        return { group: filter.value }
      case ListOutputFilterKey.input:
        return { inputName: filter.value }
      case ListOutputFilterKey.adminStatus: {
        return { adminStatus: filter.value }
      }
    }
  } else if ('values' in filter) {
    switch (filter.key) {
      case ListOutputFilterKey.appliance:
        return { appliances: filter.values.map(({ value }) => value).join(',') }
      case ListOutputFilterKey.applianceRegion:
        return { regions: filter.values.map(({ value }) => value).join(',') }
      case ListOutputFilterKey.tag:
        return { tags: filter.values.map(({ value }) => value).join(',') }
    }
  }
  return {}
}

export function mapUrlParamToOutputFilter(key: keyof OutputsRequestParams, value: string): FilterBy | undefined {
  switch (key) {
    case 'appliances':
      return {
        key: ListOutputFilterKey.appliance,
        operator: 'isOneOf',
        values: value.split(',').map((applianceName) => ({ value: applianceName, displayName: applianceName })),
      }
    case 'regions':
      return {
        key: ListOutputFilterKey.applianceRegion,
        operator: 'isOneOf',
        values: value.split(',').map((regionName) => ({ value: regionName, displayName: regionName })),
      }
    case 'inputName':
      return {
        key: ListOutputFilterKey.input,
        operator: 'is',
        value,
      }
    case 'group':
      return {
        key: ListOutputFilterKey.group,
        operator: 'is',
        value,
      }
    case 'filter':
      return {
        key: ListOutputFilterKey.name,
        operator: 'matches',
        value,
      }
    case 'adminStatus': {
      if (![OutputAdminStatus.on.toString(), OutputAdminStatus.off.toString()].includes(value)) return undefined
      return {
        key: ListOutputFilterKey.adminStatus,
        operator: 'is',
        value,
        displayName: value === OutputAdminStatus.on.toString() ? 'enabled' : 'disabled',
      }
    }
    case 'health': {
      if (![OutputFilterHealthStatus.healthy.toString(), OutputFilterHealthStatus.unhealthy.toString()].includes(value))
        return undefined
      return {
        key: ListOutputFilterKey.healthStatus,
        operator: 'is',
        value,
        displayName: value === OutputFilterHealthStatus.healthy.toString() ? 'healthy' : 'unhealthy',
      }
    }
    case 'tags': {
      return {
        key: ListOutputFilterKey.tag,
        operator: 'isOneOf',
        values: value.split(',').map((tagName) => ({ value: tagName, displayName: tagName })),
      }
    }
  }

  return undefined
}
